import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '@/utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(undefined); // undefined = still loading
  const [isGuest, setIsGuest] = useState(false);

  useEffect(() => {
    // OAuth callback in progress — AuthCallback handles the token
    if (window.location.hash?.includes('session_id=')) {
      setUser(null);
      return;
    }
    // Guest mode
    if (localStorage.getItem('da_guest') === 'true') {
      setIsGuest(true);
      setUser(null);
      return;
    }
    // Restore session — cookie is sent automatically by the browser.
    // Also supports legacy localStorage tokens via Authorization header fallback.
    api.get('/auth/me')
      .then(res => setUser(res.data))
      .catch(() => {
        // No valid session (cookie or header); clean up any legacy localStorage token
        localStorage.removeItem('da_token');
        setUser(null);
      });
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    // httpOnly cookie is set by the server response; no localStorage write
    localStorage.removeItem('da_token'); // clear any legacy token
    localStorage.removeItem('da_guest');
    setIsGuest(false);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (name, email, password, role, phone) => {
    const res = await api.post('/auth/register', { name, email, password, role, phone });
    // Cookie set server-side for diners; pending vendors get no cookie
    localStorage.removeItem('da_token');
    localStorage.removeItem('da_guest');
    setIsGuest(false);
    setUser(res.data.user);
    return res.data.user;
  };

  /** Called after Firebase / Google OAuth succeeds — cookie already set by backend response */
  const loginWithToken = (_token, userData) => {
    // Token is in the httpOnly cookie; we don't need to store it in localStorage
    localStorage.removeItem('da_token');
    localStorage.removeItem('da_guest');
    setIsGuest(false);
    setUser(userData);
  };

  const enterGuest = () => {
    localStorage.setItem('da_guest', 'true');
    localStorage.removeItem('da_token');
    setIsGuest(true);
    setUser(null);
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {} // clears httpOnly cookie server-side
    localStorage.removeItem('da_token');
    localStorage.removeItem('da_guest');
    setIsGuest(false);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, isGuest, login, register, loginWithToken, enterGuest, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
